import React from "react";
import { Phone, MapPin, Clock, Mail, Sparkles } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { LOGO_URL, PHONES } from "@/lib/brand";

export default function Footer() {
  const { t, lang, setLang } = useI18n();
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="relative border-t border-white/10 bg-black">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <a href="#top" className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl overflow-hidden bg-black ring-1 ring-white/10">
              <img src={LOGO_URL} alt="Rosas" className="w-full h-full object-cover scale-[1.6]" />
            </div>
            <div>
              <div className="font-display font-black text-xl">
                <span className="pink-text">ROSAS</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400">
                Cleaning Services
              </div>
            </div>
          </a>
          <p className="mt-6 text-sm text-zinc-400 max-w-md leading-relaxed">
            {t.footer.tagline}
          </p>
          <div className="mt-6 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-[#FF1F87] sparkle" />
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#FF66B3]">
              Clean Spaces, Better Places
            </span>
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-semibold mb-5">
            {t.footer.nav}
          </h4>
          <ul className="space-y-3 text-sm">
            <li>
              <a href="#services" data-testid="footer-services" className="text-zinc-300 hover:text-[#FF1F87] transition-colors">
                {t.nav.services}
              </a>
            </li>
            <li>
              <a href="#pricing" data-testid="footer-pricing" className="text-zinc-300 hover:text-[#FF1F87] transition-colors">
                {t.nav.pricing}
              </a>
            </li>
            <li>
              <a href="#areas" data-testid="footer-areas" className="text-zinc-300 hover:text-[#FF1F87] transition-colors">
                {t.nav.areas}
              </a>
            </li>
            <li>
              <a href="#faq" data-testid="footer-faq" className="text-zinc-300 hover:text-[#FF1F87] transition-colors">
                {t.nav.faq}
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-semibold mb-5">
            {t.footer.contact}
          </h4>
          <ul className="space-y-3 text-sm">
            {PHONES.map((p) => (
              <li key={p}>
                <a
                  href={`tel:+1${p.replace(/-/g, "")}`}
                  data-testid={`footer-phone-${p}`}
                  className="inline-flex items-center gap-2 text-zinc-200 hover:text-[#FF1F87] transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#FF1F87]" />
                  {p}
                </a>
              </li>
            ))}
            <li className="flex items-center gap-2 text-zinc-300">
              <Clock className="w-4 h-4 text-[#FF1F87]" />
              {t.footer.hours}
            </li>
            <li className="flex items-center gap-2 text-zinc-300">
              <MapPin className="w-4 h-4 text-[#FF1F87]" />
              New Jersey & New York, USA
            </li>
            <li className="flex items-center gap-2 text-zinc-300">
              <Mail className="w-4 h-4 text-[#FF1F87]" />
              info@rosascleaning.com
            </li>
          </ul>

          <div className="mt-6 flex gap-2">
            <button
              data-testid="footer-lang-es"
              onClick={() => setLang("es")}
              className={`text-xs font-semibold px-3 py-2 rounded-full border transition-all ${
                lang === "es"
                  ? "bg-[#EC008C] border-[#EC008C] text-white"
                  : "border-white/15 text-zinc-300 hover:border-[#EC008C]"
              }`}
            >
              ES
            </button>
            <button
              data-testid="footer-lang-en"
              onClick={() => setLang("en")}
              className={`text-xs font-semibold px-3 py-2 rounded-full border transition-all ${
                lang === "en"
                  ? "bg-[#EC008C] border-[#EC008C] text-white"
                  : "border-white/15 text-zinc-300 hover:border-[#EC008C]"
              }`}
            >
              EN
            </button>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-zinc-500">
            © {year} Rosas Cleaning Services. {t.footer.rights}
          </p>
          <p className="text-xs text-zinc-500">
            Bonded & Insured • NJ & NY
          </p>
        </div>
      </div>
    </footer>
  );
}
